import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, onSnapshot, query, orderBy, deleteDoc, doc, updateDoc, addDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { BUSINESS_TYPES, getBusinessType } from './businessTypes';

const Stock = () => {
  const [products, setProducts] = useState([]);
  const [, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [businessTypeFilter, setBusinessTypeFilter] = useState('All');
  const [subFilter, setSubFilter] = useState(null); // Sub Category-র নাম (কার্ডে ঢুকলে), না থাকলে null
  const [moveSub, setMoveSub] = useState(null); // { label, items } -- এই Sub Category অন্য Main Category-তে সরানোর মডাল
  const [moveTo, setMoveTo] = useState('');
  const [moving, setMoving] = useState(false);

  const formatTk = (num) => new Intl.NumberFormat('en-IN').format(Math.ceil(num || 0));
  const formatDateTime = (dStr) => dStr ? new Date(dStr).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'2-digit', hour:'2-digit', minute:'2-digit', hour12:true }).replace(',','') : "N/A";

  useEffect(() => {
    const q = query(collection(db, "products"), orderBy("name", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });
    
    const handleEsc = (e) => { if (e.key === 'Escape') { setSelectedProduct(null); setIsScannerOpen(false); setMoveSub(null); } };
    window.addEventListener('keydown', handleEsc);
    return () => { unsubscribe(); window.removeEventListener('keydown', handleEsc); };
  }, []);

  const exportStockToExcel = () => {
    const data = rows.map((p, i) => ({
      SL: i + 1,
      Name: p.name,
      BusinessType: getBusinessType(p),
      Category: p.category,
      Price: p.price,
      Stock: p.stock,
      TotalValue: p.price * p.stock,
      Barcode: p.barcode || 'N/A'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stock");
    XLSX.writeFile(wb, `Stock_Report_${new Date().toLocaleDateString()}.xlsx`);
  };

  // নির্দিষ্ট স্টক হিস্ট্রি লগ ডিলিট করার হ্যান্ডলার (রিসাইকেল বিন ও সঠিক স্টক আপডেটসহ)
  const deleteSpecificLog = async (product, logIndex) => {
    const sortedHistory = [...(product.history || [])].sort((a, b) => new Date(b.date) - new Date(a.date));
    const targetLog = sortedHistory[logIndex];
    
    const logDate = targetLog?.date ? formatDateTime(targetLog.date) : 'N/A';
    const logQty = targetLog?.quantity || 0;
    const logRate = targetLog?.costPrice || targetLog?.price || 0;

    const confirmMsg = `Delete this entry?\n\nProduct: ${product.name}\nDate: ${logDate}\nQty: ${logQty} Pcs\n\nStock will be updated and moved to Recycle Bin.`;

    if (!window.confirm(confirmMsg)) return;

    // মূল হিস্ট্রি থেকে আসল লগটি বাদ দেওয়া
    const newHist = product.history.filter(h => h !== targetLog);
    const calculatedStock = newHist.reduce((sum, e) => sum + (Number(e.quantity) || 0), 0);
    
    try {
      const productRef = doc(db, "products", product.id);

      // রিসাইকেল বিনে সঠিক ফরম্যাটে ডাটা সেভ করা যাতে রিসাইকেল বিন পেজে সুন্দরভাবে দেখায়
      await addDoc(collection(db, "deleted_products"), {
        type: "stock_log", // রিসাইকেল বিনের ক্যাটাগরির জন্য
        name: product.name,
        barcode: product.barcode || 'N/A',
        deletedItemType: `Stock Log (${product.name})`,
        deletedLog: targetLog,
        originalCollection: "products",
        originalId: product.id,
        deletedAt: serverTimestamp(),
        deletedBy: auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Staff',
        quantity: logQty,
        amount: logRate * logQty,
        reason: `Deleted from Stock History of ${product.name}`
      });

      if (newHist.length === 0) {
        await deleteDoc(productRef);
      } else {
        await updateDoc(productRef, { history: newHist, stock: calculatedStock });
      }
      setSelectedProduct(null);
      alert("Successfully sent to Recycle Bin and stock updated!");
    } catch (err) { 
      console.error(err);
      alert("Error!"); 
    }
  };

  const softDelete = async (p) => {
    if(window.confirm(`Delete ${p.name}?`)) {
      await addDoc(collection(db, "deleted_products"), { 
        ...p, 
        type: "product",
        deletedItemType: "Product",
        deletedAt: serverTimestamp() 
      });
      await deleteDoc(doc(db, "products", p.id));
    }
  };

  // Sub Category (p.category); না থাকলে "General" (Sales পেজের মতোই)। বড়-ছোট হাতের অক্ষর/বাড়তি স্পেসের
  // ভিন্নতা এক ধরা হয় (Purchase পেজও তাই করে) -- তুলনার সময় সবসময় .toLowerCase() করা হয়।
  const subOf = (p) => (p.category || '').trim() || 'General';

  const filtered = products.filter(p => {
    const matchSearch = p.name?.toLowerCase().includes(searchTerm.toLowerCase()) || p.barcode?.includes(searchTerm);
    const matchType = businessTypeFilter === 'All' || getBusinessType(p) === businessTypeFilter;
    const matchSub = !subFilter || subOf(p).toLowerCase() === subFilter.toLowerCase();
    if (!matchType || !matchSub) return false;

    if (!startDate && !endDate) return matchSearch;

    const start = startDate ? new Date(startDate) : new Date('1970-01-01');
    start.setHours(0, 0, 0, 0);

    const end = endDate ? new Date(endDate) : new Date();
    end.setHours(23, 59, 59, 999);

    const historyDates = (p.history || []).map(h => new Date(h.date));
    const matchInHistory = historyDates.some(d => d >= start && d <= end);
    const createdAtDate = p.createdAt?.seconds ? new Date(p.createdAt.seconds * 1000) : null;
    const matchCreatedAt = createdAtDate ? (createdAtDate >= start && createdAtDate <= end) : false;

    return matchSearch && (matchInHistory || matchCreatedAt || (!startDate && !endDate));
  });

  const totalValue = filtered.reduce((acc, p) => acc + (p.price * p.stock), 0);

  // তিন ধাপ: (১) Main Category কার্ড (Saree/Panjabi...) -> (২) সেই ধরনের Sub Category কার্ড (Katan/Jamdani...)
  // -> (৩) প্রোডাক্টের তালিকা। সার্চ করলে যেকোনো ধাপ থেকেই সরাসরি তালিকা (ওই ধাপের সীমার মধ্যে) দেখায়।
  const searching = searchTerm.trim() !== '';
  const isLanding = businessTypeFilter === 'All' && !searching;
  const isSubCards = businessTypeFilter !== 'All' && !subFilter && !searching;
  const isList = !isLanding && !isSubCards;

  const sumStock = (items) => items.reduce((s, p) => s + (Number(p.stock) || 0), 0);
  const allValue = products.reduce((acc, p) => acc + (p.price * p.stock || 0), 0);

  // কার্ড BUSINESS_TYPES-এর ক্রমে; তালিকার বাইরের কোনো ধরন থাকলে সেটা শেষে যাবে, যাতে কোনো প্রোডাক্ট
  // হারিয়ে না যায়। প্রোডাক্ট নেই এমন ধরনের কার্ড দেখানো হয় না।
  const cards = (() => {
    const byType = {};
    products.forEach(p => { const t = getBusinessType(p); (byType[t] = byType[t] || []).push(p); });
    const order = [...BUSINESS_TYPES, ...Object.keys(byType).filter(t => !BUSINESS_TYPES.includes(t))];
    return order.filter(t => byType[t]).map(t => ({ type: t, count: byType[t].length, stock: sumStock(byType[t]) }));
  })();

  // নির্বাচিত Main Category-র ভেতরের Sub Category কার্ড; Sub Category না থাকলে "General" সবার শেষে
  const subCards = (() => {
    if (businessTypeFilter === 'All') return [];
    const bySub = {};
    products.filter(p => getBusinessType(p) === businessTypeFilter).forEach(p => {
      const label = subOf(p);
      const key = label.toLowerCase();
      (bySub[key] = bySub[key] || { key, label, items: [] }).items.push(p);
    });
    return Object.values(bySub)
      .sort((a, b) => ((a.key === 'general') - (b.key === 'general')) || a.key.localeCompare(b.key))
      .map(s => ({ ...s, count: s.items.length, stock: sumStock(s.items) }));
  })();

  const openMainCard = (type) => { setBusinessTypeFilter(type); setSubFilter(null); };

  // ব্যাক: Sub Category তালিকা -> Main Category-র Sub Category কার্ড -> সব-স্টক কার্ড
  const backInPlace = subFilter || (businessTypeFilter !== 'All' && searching);
  const goBack = () => {
    setSearchTerm(''); setStartDate(''); setEndDate('');
    if (subFilter) setSubFilter(null);
    else if (!(businessTypeFilter !== 'All' && searching)) setBusinessTypeFilter('All');
  };

  // একটা Sub Category-র সব প্রোডাক্ট অন্য Main Category-তে সরানো (যেমন ভুল করে Saree-তে ঢুকে থাকা Dress)।
  // শুধু প্রোডাক্টের businessType বদলায় -- স্টক, দাম, আগের বিক্রি/পার্চেজের রেকর্ড অপরিবর্তিত থাকে।
  const confirmMove = async () => {
    if (!moveSub || !moveTo) return;
    const clashes = moveSub.items.filter(p => products.some(q =>
      q.id !== p.id && getBusinessType(q) === moveTo && (q.name || '').toLowerCase().trim() === (p.name || '').toLowerCase().trim()
    ));
    if (clashes.length) {
      return alert(`Can't move — these names already exist under ${moveTo}:\n${clashes.map(p => p.name).join(', ')}`);
    }
    setMoving(true);
    try {
      // একটা batch-এ ৫০০-র বেশি লেখা যায় না, তাই ভাগ করে
      for (let i = 0; i < moveSub.items.length; i += 400) {
        const batch = writeBatch(db);
        moveSub.items.slice(i, i + 400).forEach(p => batch.update(doc(db, 'products', p.id), { businessType: moveTo }));
        await batch.commit();
      }
      alert(`Moved ${moveSub.items.length} product${moveSub.items.length === 1 ? '' : 's'} to ${moveTo}.`);
      setMoveSub(null);
      setMoveTo('');
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setMoving(false);
  };

  // তালিকা: Sub Category অনুযায়ী পাশাপাশি, তারপর নাম অনুযায়ী -- Sub Category না থাকলে "General" সবার শেষে।
  const typeRank = (p) => { const i = BUSINESS_TYPES.indexOf(getBusinessType(p)); return i === -1 ? 999 : i; };
  const rows = [...filtered].sort((a, b) =>
    (typeRank(a) - typeRank(b)) ||
    ((subOf(a).toLowerCase() === 'general') - (subOf(b).toLowerCase() === 'general')) ||
    subOf(a).toLowerCase().localeCompare(subOf(b).toLowerCase()) ||
    (a.name || '').localeCompare(b.name || '')
  );

  const title = isLanding ? 'Stock Inventory'
    : businessTypeFilter === 'All' ? 'Search Results'
    : subFilter ? `${businessTypeFilter} › ${subFilter}`
    : businessTypeFilter;
  const valueLabel = isLanding ? 'TOTAL STOCK VALUE'
    : (businessTypeFilter === 'All' || searching || startDate || endDate) ? 'FILTERED VALUE'
    : `${(subFilter || businessTypeFilter).toUpperCase()} VALUE`;

  const cardStyle = { textAlign:'left', cursor:'pointer', borderRadius:'20px', border:'1px solid #e6dfff', background:'linear-gradient(135deg, #f4f0ff, #ffffff)', boxShadow:'0 4px 14px rgba(74,0,224,0.08)' };

  return (
    <div className="white-box fade-in">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'25px', flexWrap:'wrap', gap:'15px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          {!isLanding && (
            <button onClick={goBack} style={{padding:'8px 14px', background:'#f4f0ff', color:'#4a00e0', border:'none', borderRadius:'10px', cursor:'pointer', fontWeight:'bold', fontSize:'13px'}}>
              ← {backInPlace ? businessTypeFilter : 'All Stock'}
            </button>
          )}
          <h2 style={{margin:0}}>{title}</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
           <div style={{textAlign:'right', marginRight:'5px'}}>
              <small style={{color:'#888'}}>{valueLabel}</small>
              <h3 style={{margin:0, color:'#4a00e0'}}>৳ {formatTk(isLanding ? allValue : totalValue)}</h3>
           </div>

           {isList && (
             <>
               <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} style={{padding:'8px', borderRadius:'10px', border:'1px solid #eee'}} />
               <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} style={{padding:'8px', borderRadius:'10px', border:'1px solid #eee'}} />

               {(startDate || endDate) && (
                 <button
                   onClick={() => { setStartDate(''); setEndDate(''); }}
                   style={{padding:'8px 12px', background:'#e74c3c', color:'#fff', border:'none', borderRadius:'10px', cursor:'pointer', fontWeight:'bold', fontSize:'12px'}}
                 >
                   Clear Date
                 </button>
               )}
             </>
           )}

           <button onClick={exportStockToExcel} style={{background:'#27ae60', color:'#fff', border:'none', padding:'10px 15px', borderRadius:'10px', cursor:'pointer', fontWeight:'bold'}}>📥 Excel</button>

           <div style={{display:'flex', background:'#f8f9ff', borderRadius:'12px', padding:'5px 10px', border:'1px solid #eee'}}>
              <input value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} placeholder="Search..." style={{border:'none', background:'transparent', outline:'none', width:'120px'}} />
              <button onClick={()=>setIsScannerOpen(!isScannerOpen)} style={{border:'none', background:'none', cursor:'pointer'}}>📷</button>
           </div>
        </div>
      </div>

      {isLanding && (
        cards.length === 0 ? (
          <div style={{textAlign:'center', color:'#999', padding:'40px'}}>No products in stock yet.</div>
        ) : (
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:'20px'}}>
            {cards.map(c => (
              <button key={c.type} className="hover-card" onClick={() => openMainCard(c.type)} style={{...cardStyle, padding:'28px 24px'}}>
                <div style={{fontSize:'22px', fontWeight:'bold', color:'#4a00e0'}}>{c.type}</div>
                <div style={{marginTop:'14px', color:'#666', fontSize:'14px'}}>{c.count} product{c.count === 1 ? '' : 's'}</div>
                <div style={{marginTop:'2px', color:'#666', fontSize:'14px'}}>{c.stock} pcs in stock</div>
              </button>
            ))}
          </div>
        )
      )}

      {isSubCards && (
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:'18px'}}>
          {subCards.map(s => (
            <div
              key={s.key}
              className="hover-card"
              role="button"
              tabIndex={0}
              onClick={() => setSubFilter(s.label)}
              onKeyDown={e => { if (e.key === 'Enter') setSubFilter(s.label); }}
              style={{...cardStyle, padding:'22px 20px'}}
            >
              <div style={{fontSize:'19px', fontWeight:'bold', color:'#4a00e0'}}>{s.label}</div>
              <div style={{marginTop:'12px', color:'#666', fontSize:'14px'}}>{s.count} product{s.count === 1 ? '' : 's'}</div>
              <div style={{marginTop:'2px', color:'#666', fontSize:'14px'}}>{s.stock} pcs in stock</div>
              <button
                type="button"
                onClick={e => { e.stopPropagation(); setMoveSub(s); setMoveTo(''); }}
                style={{marginTop:'14px', padding:0, border:'none', background:'none', color:'#8a7fd1', fontSize:'11px', cursor:'pointer', textDecoration:'underline'}}
              >
                Move to another category
              </button>
            </div>
          ))}
        </div>
      )}

      {isList && (
      <table style={{width:'100%'}}>
        <thead><tr style={{textAlign:'left', color:'#888', borderBottom:'2px solid #f4f7fe'}}><th>SL</th><th>Name</th><th>Price</th><th>Stock</th><th>Balance</th><th>Action</th></tr></thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={p.id} style={{borderBottom:'1px solid #f9f9f9'}}>
              <td style={{padding:'15px', color:'#ccc'}}>{i+1}</td>
              <td>
                <b>{p.name}</b>
                {/* Sales পেজের মতো: নামের নিচে Sub Category ট্যাগ; সব-ধরনের সার্চ রেজাল্টে Main Category-ও দেখায় */}
                <div style={{fontSize:'11px', color:'#666', marginTop:'2px'}}>
                  {businessTypeFilter === 'All' && (
                    <span style={{background:'#eee', padding:'2px 6px', borderRadius:'4px', color:'#555', fontWeight:'bold', marginRight:'4px'}}>{getBusinessType(p)}</span>
                  )}
                  {!subFilter && (
                    <span style={{background:'#f0f0ff', padding:'2px 6px', borderRadius:'4px', color:'#4e43e7', fontWeight:'bold', marginRight:'4px'}}>{subOf(p)}</span>
                  )}
                  <span style={{color:'#aaa'}}>#{p.barcode||'No ID'}</span>
                </div>
              </td>
              <td>৳ {formatTk(p.price)}</td>
              <td><span style={{background: p.stock > 5 ? '#e8f5e9' : '#ffeded', color: p.stock > 5 ? '#2e7d32' : '#d32f2f', padding:'3px 8px', borderRadius:'8px', fontWeight: 'bold'}}>{p.stock}</span></td>
              <td style={{fontWeight:'bold', color:'#4a00e0'}}>৳ {formatTk(p.price * p.stock)}</td>
              <td>
                <button onClick={()=>setSelectedProduct(p)} style={{border:'none', background:'#f4f7fe', color:'#4a00e0', padding:'5px 10px', borderRadius:'10px', cursor:'pointer', fontWeight:'bold', marginRight:'10px'}}>Logs</button>
                <button onClick={()=>softDelete(p)} style={{border:'none', background:'none', cursor:'pointer'}}>🗑️</button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={6} style={{textAlign:'center', padding:'30px', color:'#999'}}>No products found.</td></tr>
          )}
        </tbody>
      </table>
      )}

      {moveSub && (
        <div className="modal-overlay" onClick={() => !moving && setMoveSub(null)}>
          <div className="white-box" onClick={e=>e.stopPropagation()} style={{width:'440px', maxWidth:'92vw'}}>
            <h3 style={{marginTop:0}}>Move "{moveSub.label}" to another category</h3>
            <p style={{fontSize:'13px', color:'#666', lineHeight:1.5}}>
              All {moveSub.items.length} product{moveSub.items.length === 1 ? '' : 's'} under <b>{moveSub.label}</b> will move out of <b>{businessTypeFilter}</b> into the Main Category you pick.
              Stock, prices and past sales/purchases are not changed. You can move them back the same way.
            </p>
            <select value={moveTo} onChange={e=>setMoveTo(e.target.value)} className="input-field">
              <option value="">-- Select Main Category --</option>
              {BUSINESS_TYPES.filter(t => t !== businessTypeFilter).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <div style={{display:'flex', gap:'10px', marginTop:'15px'}}>
              <button onClick={() => setMoveSub(null)} disabled={moving} style={{flex:1, padding:'12px', background:'#f4f4f4', color:'#555', border:'none', borderRadius:'12px', cursor:'pointer', fontWeight:'bold'}}>Cancel</button>
              <button onClick={confirmMove} disabled={!moveTo || moving} style={{flex:1, padding:'12px', background:'#4a00e0', color:'#fff', border:'none', borderRadius:'12px', cursor:'pointer', fontWeight:'bold', opacity: (!moveTo || moving) ? 0.5 : 1}}>
                {moving ? 'Moving...' : 'Move'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedProduct && (
        <div className="modal-overlay" onClick={()=>setSelectedProduct(null)}>
          <div className="white-box" onClick={e=>e.stopPropagation()} style={{width:'600px'}}>
            <h3 style={{marginTop:0}}>History: {selectedProduct.name} <button onClick={()=>setSelectedProduct(null)} style={{float:'right', border:'none', color:'red', background:'none', cursor:'pointer', fontWeight:'bold'}}>X</button></h3>
            <div style={{maxHeight:'300px', overflowY:'auto'}}>
              <table style={{width:'100%', marginTop:'20px', borderCollapse:'collapse'}}>
                <thead style={{background:'#f4f7fe'}}>
                  <tr>
                    <th style={{padding:'8px', fontSize:'13px'}}>Date & Time</th>
                    <th style={{padding:'8px', fontSize:'13px'}}>Rate</th>
                    <th style={{padding:'8px', fontSize:'13px'}}>Qty</th>
                    <th style={{padding:'8px', fontSize:'13px'}}>Added By</th>
                    <th style={{padding:'8px', fontSize:'13px'}}>Del</th>
                  </tr>
                </thead>
                <tbody>
                  {/* সাম্প্রতিক (নতুন) ডেট উপরে দেখানোর জন্য সর্ট করা হলো */}
                  {[(selectedProduct.history || [])].flat()
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((h, i) => (
                    <tr key={i} style={{textAlign:'center', borderBottom:'1px solid #eee'}}>
                      <td style={{padding:'10px', fontSize:'11px'}}>{formatDateTime(h.date)}</td>
                      <td style={{fontWeight:'bold', fontSize:'12px'}}>৳ {formatTk(h.costPrice || h.price)}</td>
                      <td style={{fontSize:'12px'}}>{h.quantity}</td>
                      <td style={{fontSize:'12px', color:'#4a00e0', fontWeight:'500'}}>{h.addedBy || 'Sohan'}</td>
                      <td><button onClick={()=>deleteSpecificLog(selectedProduct, i)} style={{border:'none', background:'none', cursor:'pointer'}}>❌</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stock;